import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Building2, ChevronDown, Plus, Check } from 'lucide-react';
import { toast } from 'sonner';

const ROLE_LABEL = {
  resident: 'Resident', trustee: 'Trustee', chairman: 'Chairman',
  property_manager: 'Property Manager', managing_agent: 'Managing Agent',
  agent_admin: 'Agent Admin', super_agent: 'Super Agent',
  platform_owner: 'Platform Owner',
};

/**
 * Building Switcher dropdown — drop into any top nav.
 * Shows the current building plus a list of all linked buildings.
 * Recent buildings are pinned to the top.
 */
export default function BuildingSwitcher({ compact = false }) {
  const { myBuildings, activeBuilding, switchBuilding } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [switching, setSwitching] = useState(null);

  if (!myBuildings || myBuildings.length === 0) {
    return (
      <Button
        size={compact ? 'sm' : 'default'}
        variant="outline"
        onClick={() => navigate('/find-building')}
        data-testid="add-first-building-btn"
      >
        <Plus className="w-4 h-4 mr-1" /> Add Building
      </Button>
    );
  }

  const handleSwitch = async (hoa_id) => {
    if (hoa_id === activeBuilding?.hoa_id) {
      setOpen(false);
      return;
    }
    setSwitching(hoa_id);
    const ok = await switchBuilding(hoa_id);
    setSwitching(null);
    setOpen(false);
    if (ok) {
      const b = myBuildings.find((x) => x.hoa_id === hoa_id);
      toast.success(`Switched to ${b?.building_name || 'building'}`);
    } else {
      toast.error('Could not switch building');
    }
  };

  return (
    <div className="relative inline-block text-left" data-testid="building-switcher">
      <button
        onClick={() => setOpen((o) => !o)}
        className={`flex items-center gap-2 px-3 py-1.5 rounded-md border bg-white hover:bg-gray-50 text-sm ${compact ? 'max-w-[180px]' : 'max-w-[260px]'}`}
        data-testid="building-switcher-trigger"
      >
        <Building2 className="w-4 h-4 text-indigo-600 shrink-0" />
        <span className="truncate font-medium">
          {activeBuilding?.building_name || 'Choose building'}
        </span>
        {activeBuilding?.is_primary_admin && (
          <Badge variant="outline" className="bg-indigo-50 text-indigo-700 border-indigo-200 text-xs hidden sm:inline">Primary</Badge>
        )}
        <ChevronDown className="w-4 h-4 ml-auto text-gray-400 shrink-0" />
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-20" onClick={() => setOpen(false)} />
          <div className="absolute right-0 mt-1 w-80 bg-white border rounded-lg shadow-lg z-30 max-h-[450px] overflow-y-auto" data-testid="building-switcher-menu">
            <div className="px-3 py-2 text-xs text-gray-500 uppercase tracking-wide border-b">
              Your buildings ({myBuildings.length})
            </div>
            {myBuildings.map((b) => {
              const isActive = b.hoa_id === activeBuilding?.hoa_id;
              return (
                <button
                  key={b.hoa_id}
                  onClick={() => handleSwitch(b.hoa_id)}
                  disabled={switching === b.hoa_id}
                  className={`w-full text-left px-3 py-2.5 border-b last:border-0 hover:bg-indigo-50 ${isActive ? 'bg-indigo-50/50' : ''}`}
                  data-testid={`switch-to-${b.hoa_id}`}
                >
                  <div className="flex items-start gap-2">
                    <Building2 className={`w-4 h-4 mt-0.5 shrink-0 ${isActive ? 'text-indigo-600' : 'text-gray-400'}`} />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm truncate flex items-center gap-2">
                        {b.building_name}
                        {isActive && <Check className="w-3 h-3 text-indigo-600 shrink-0" />}
                      </div>
                      <div className="text-xs text-gray-500 truncate">
                        {ROLE_LABEL[b.role] || b.role}
                        {b.unit_number ? ` · Unit ${b.unit_number}` : ''}
                        {b.city ? ` · ${b.city}` : ''}
                      </div>
                    </div>
                    {b.is_primary_admin && (
                      <Badge variant="outline" className="bg-indigo-50 text-indigo-700 border-indigo-200 text-[10px] shrink-0">Primary</Badge>
                    )}
                  </div>
                </button>
              );
            })}
            <button
              onClick={() => { setOpen(false); navigate('/my-buildings'); }}
              className="w-full text-left px-3 py-2 text-sm text-indigo-600 hover:bg-gray-50 border-t font-medium"
              data-testid="manage-buildings-btn"
            >
              Manage all buildings →
            </button>
            <button
              onClick={() => { setOpen(false); navigate('/find-building'); }}
              className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 border-t flex items-center gap-2"
              data-testid="add-building-btn"
            >
              <Plus className="w-4 h-4" /> Add another building
            </button>
          </div>
        </>
      )}
    </div>
  );
}
